import { useState, useCallback } from 'react';
import { UserLocation, GeolocationError } from '../types/trip.types';

interface UseGeolocationReturn {
  location: UserLocation | null;
  isLoading: boolean;
  error: GeolocationError | null;
  getCurrentLocation: () => Promise<UserLocation>;
  clearError: () => void;
}

const GEOLOCATION_ERRORS: Record<number, string> = {
  1: 'Location access denied by user',
  2: 'Location information is unavailable',
  3: 'Location request timeout',
};

export const useGeolocation = (): UseGeolocationReturn => {
  const [location, setLocation] = useState<UserLocation | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<GeolocationError | null>(null);

  const clearError = useCallback(() => {
    setError(null);
  }, []);

  const getCurrentLocation = useCallback((): Promise<UserLocation> => {
    return new Promise((resolve, reject) => {
      if (!navigator.geolocation) {
        const geolocationError: GeolocationError = {
          code: 0,
          message: 'Geolocation is not supported by this browser',
        };
        setError(geolocationError);
        reject(geolocationError);
        return;
      }

      setIsLoading(true);
      setError(null);

      const options: PositionOptions = {
        enableHighAccuracy: true,
        timeout: 10000,
        maximumAge: 60000,
      };

      navigator.geolocation.getCurrentPosition(
        async position => {
          const { latitude, longitude } = position.coords;

          const userLocation: UserLocation = {
            latitude,
            longitude,
          };

          try {
            const address = await reverseGeocode(latitude, longitude);
            userLocation.address = address;
          } catch (geocodingError) {
            console.warn('Reverse geocoding failed:', geocodingError);
          }

          setLocation(userLocation);
          setIsLoading(false);
          resolve(userLocation);
        },
        positionError => {
          const geolocationError: GeolocationError = {
            code: positionError.code,
            message: GEOLOCATION_ERRORS[positionError.code] || positionError.message,
          };

          setError(geolocationError);
          setIsLoading(false);
          reject(geolocationError);
        },
        options
      );
    });
  }, []);

  return {
    location,
    isLoading,
    error,
    getCurrentLocation,
    clearError,
  };
};

const reverseGeocode = async (latitude: number, longitude: number): Promise<string> => {
  try {
    const response = await fetch(
      `https://nominatim.openstreetmap.org/reverse?format=json&lat=${latitude}&lon=${longitude}`
    );

    if (!response.ok) {
      throw new Error('Reverse geocoding request failed');
    }

    const data = await response.json();
    return data.display_name || `${latitude}, ${longitude}`;
  } catch (error) {
    throw new Error('Failed to get address from coordinates');
  }
};
